import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlarmClock, BarChart3, CalendarDays, Check, CheckCheck, ChevronLeft, ChevronRight, ClipboardList, Download, Eye, ListChecks, Palmtree,
  Pencil, Percent, Plus, Search, Send, ShieldCheck, Timer, UserCheck, UserX, X,
} from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { DateInput, Select } from '../components/Controls';
import { download, fmtNumber, t12, titleCase, toCSV } from '../format';
import Icon from '../Icon';
import { Chip, Kpi, fmtDay, hhmm, monthLabel, shiftDay, shiftMonth, useFrozen } from './attendance/shared';

// Time & Attendance > Attendance Management > Attendance (Stephanet's AttendanceManagement): the daily register with
// Mark Attendance, the site/day approval queue (Pending Review → Pre-Approved → Awaiting Final Approval → Approved,
// run in the approval engine) and the monthly attendance analysis. Hours, overtime and lateness are worked out on the
// server from the clock times against the employee's shift; the form previews them with the company's default shift.

const STATUS = {
  present: ['Present', 'valid', 'green'], late: ['Late', 'd90', 'yellow'], absent: ['Absent', 'd30', 'red'], leave: ['Leave', 'd180', 'blue'],
  sick: ['Sick', 'd180', 'teal'], holiday: ['Holiday', 'none', 'purple'], off: ['Off', 'none', 'gray'],
};
const shown = r => (r.status === 'present' && r.is_late ? 'late' : r.status);
const BATCH = {
  unsubmitted: ['Not Submitted', 'gray'], pending: ['Pending Review', 'yellow'], pre_approved: ['Pre-Approved', 'blue'],
  reviewed: ['Awaiting Final Approval', 'purple'], approved: ['Approved', 'green'], rejected: ['Rejected', 'red'],
};
const OPEN = ['pending', 'pre_approved', 'reviewed'];
const TABS = [['register', 'Daily Register', ListChecks], ['queue', 'Approval Queue', CheckCheck], ['analysis', 'Analysis', BarChart3]];

export default function AttendanceManagement() {
  const { notify } = useHR();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'register';
  const [day, setDay] = useState(params.get('d') || '');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [marking, setMarking] = useState(null);
  const [tick, setTick] = useState(0);

  // with no date chosen the server opens on the latest day with attendance
  const load = useCallback(() => hr.getAttendanceDay(day || undefined)
    .then(d => { setData(d); setError(''); setTick(t => t + 1); if (!day) setDay(d.date); })
    .catch(e => setError(e.message)), [day]);
  useEffect(() => { load(); }, [load]);
  const setTab = t => setParams(t === 'register' ? {} : { tab: t });

  if (error && !data) return <div className="error">{error}</div>;
  if (!data || !day) return <Loader title="Loading attendance…" subtitle="Fetching the day's register" />;

  const rows = data.rows;
  const total = rows.length;
  const pct = n => `${total ? Math.round((100 * n) / total) : 0}% of total`;
  const k = {
    present: rows.filter(r => r.status === 'present').length, absent: rows.filter(r => r.status === 'absent').length,
    leave: rows.filter(r => ['leave', 'sick'].includes(r.status)).length, late: rows.filter(r => r.is_late).length,
    ot: rows.filter(r => Number(r.overtime_hours) > 0).length,
  };
  const exportDay = () => download(`attendance-${day}.csv`, toCSV([
    { label: 'Employee', get: r => r.full_name }, { label: 'Employee ID', get: r => r.employee_no }, { label: 'Department', get: r => r.department },
    { label: 'Site', get: r => r.site }, { label: 'Time In', get: r => t12(r.time_in) }, { label: 'Time Out', get: r => t12(r.time_out) },
    { label: 'Hours Worked', get: r => r.hours_worked }, { label: 'Overtime (h)', get: r => r.overtime_hours }, { label: 'Status', get: r => STATUS[shown(r)]?.[0] },
    { label: 'Reason', get: r => r.reason }, { label: 'Source', get: r => r.source },
  ], rows));

  return (
    <div className="mdr mds att2">
      <div className="mdr-top">
        <div>
          <h2>Attendance Management</h2>
          <p>Daily attendance tracking, approvals, and workforce analysis{data.holiday ? ` · ${data.holiday}` : ''}</p>
        </div>
        <div className="mdr-top-actions att2-actions no-print">
          <div className="att2-day">
            <button type="button" title="Previous day" onClick={() => setDay(shiftDay(day, -1))}><ChevronLeft size={16} /></button>
            <DateInput value={day} onChange={e => e.target.value && setDay(e.target.value)} style={{ minWidth: 150 }} />
            <button type="button" title="Next day" onClick={() => setDay(shiftDay(day, 1))}><ChevronRight size={16} /></button>
          </div>
          <button className="mdr-btn" onClick={exportDay} disabled={!rows.length}><Download size={14} /> Export</button>
          {data.can_edit && <button className="mdr-btn att2-mark" onClick={() => setMarking({})}><Plus size={14} /> Mark Attendance</button>}
        </div>
      </div>
      <p className="print-only mds-print-period">{fmtDay(day)}</p>

      <div className="md-kpis mds-kpis att2-kpis" key={`k${tick}`}>
        {[['Present', k.present, UserCheck, 'green'], ['Absent', k.absent, UserX, 'red'], ['On Leave', k.leave, Palmtree, 'blue'],
          ['Late', k.late, AlarmClock, 'amber'], ['With Overtime', k.ot, Timer, 'purple']]
          .map(([label, value, Ico, tone], i) => <Kpi key={label} {...{ label, value, Ico, tone, i }} note={pct(value)} />)}
      </div>

      <div className="mds-tabs no-print" role="tablist">
        {TABS.map(([key, label, Ico]) => (
          <button key={key} role="tab" className={tab === key ? 'active' : ''} onClick={() => setTab(key)}><Ico size={14} /> {label}</button>
        ))}
      </div>

      {tab === 'register' && <Register data={data} day={day} tick={tick} onEdit={r => setMarking(r)} onReload={load} notify={notify} />}
      {tab === 'queue' && <Queue notify={notify} onOpen={d => { setDay(d); setParams({}); }} />}
      {tab === 'analysis' && <Analysis month={day.slice(0, 7)} />}

      {marking && <MarkModal data={data} day={day} record={marking.id ? marking : null} onClose={() => setMarking(null)}
                             onSaved={() => { setMarking(null); load(); }} notify={notify} />}
    </div>
  );
}

// ---------------------------------------------------------------- daily register
function Register({ data, day, tick, onEdit, onReload, notify }) {
  const [q, setQ] = useState('');
  const [site, setSite] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const rows = data.rows.filter(r => {
    if (site && String(r.site_id) !== site) return false;
    if (status === 'overtime' ? !(Number(r.overtime_hours) > 0) : status && shown(r) !== status) return false;
    return !q || [r.full_name, r.employee_no, r.department].some(v => String(v || '').toLowerCase().includes(q.toLowerCase()));
  });
  const ref = useFrozen([tick, rows.length, data.can_edit]);
  const markAllPresent = async () => {
    setBusy(true);
    try {
      const res = await hr.markAllPresent({ work_date: day });
      notify(res.message);
    } catch (err) { notify(err.message, 'bad'); }
    setBusy(false); setConfirm(false); onReload();
  };
  const cols = data.can_edit ? 12 : 11;
  const shift = `${data.policy.shift_start}–${data.policy.shift_end}`;
  return (
    <div className="mdr-card mdr-table-card">
      <div className="mds-table-head">
        <b>Daily Register <span className="mdl-year">{fmtDay(day)}</span></b>
        <div className="att2-filters no-print">
          <div className="mdr-search mds-quick"><Search size={14} /><input placeholder="Search employees..." value={q} onChange={e => setQ(e.target.value)} /></div>
          <Select value={site} onChange={e => setSite(e.target.value)} title="Site" style={{ minWidth: 150 }}>
            <option value="">All Sites</option>{data.sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <Select value={status} onChange={e => setStatus(e.target.value)} title="Status" style={{ minWidth: 120 }}>
            <option value="">All</option>{Object.entries(STATUS).map(([key, [l]]) => <option key={key} value={key}>{l}</option>)}<option value="overtime">With Overtime</option>
          </Select>
          <span className="att2-count">{rows.length} employees</span>
        </div>
      </div>
      {data.unmarked.length > 0 && data.can_edit && (
        <div className="att2-unmarked no-print">
          <span><UserX size={15} /> {data.unmarked.length} active employee{data.unmarked.length > 1 ? 's have' : ' has'} no attendance for this day.</span>
          <button className="mds-run" disabled={busy} onClick={() => setConfirm(true)}><Check size={14} /> Mark all present</button>
        </div>
      )}
      <div className="mdr-scroll">
        <table className="mdr-table mds-table mds-frozen att2-table" ref={ref}>
          <thead><tr>
            {data.can_edit && <th className="c no-print">Action</th>}
            <th>Employee ID</th><th>Employee Name</th><th>Department</th><th>Site</th><th className="c">Time In</th><th className="c">Time Out</th>
            <th className="c">Hours</th><th className="c">Overtime</th><th>Status</th><th>Reason</th><th>Source</th>
          </tr></thead>
          <tbody key={tick}>
            {rows.map((r, i) => {
              const [label, , tone] = STATUS[shown(r)] || [titleCase(r.status), 'none', 'gray'];
              return (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 18}ms` }}>
                  {data.can_edit && <td className="c no-print"><button className="mds-view" title="Edit attendance" onClick={() => onEdit(r)}><Pencil size={12} /> Edit</button></td>}
                  <td className="mdr-emp">{r.employee_no}</td>
                  <td className="mdr-name att2-name">{r.full_name}</td>
                  <td className="nowrap">{r.department || '—'}</td>
                  <td>{r.site ? <span className="att2-site">{r.site}</span> : '—'}</td>
                  <td className="c mono">{t12(r.time_in) || '—'}</td><td className="c mono" title={r.time_out_next_day ? 'Next day' : undefined}>{t12(r.time_out) || '—'}{r.time_out_next_day && r.time_out ? <sup> +1</sup> : null}</td>
                  <td className="c mono">{r.status === 'present' ? hhmm(r.hours_worked) : '—'}</td>
                  <td className={`c mono ${Number(r.overtime_hours) > 0 ? 'in' : ''}`}>{Number(r.overtime_hours) > 0 ? hhmm(r.overtime_hours) : '—'}</td>
                  <td><Chip tone={tone}>{label.toUpperCase()}</Chip></td>
                  <td className="att2-reason">{r.reason || '—'}</td>
                  <td className="nowrap">{titleCase(r.source || '') || '—'}</td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={cols} className="mdr-empty">No attendance recorded for this day{site || status || q ? ' with these filters' : ''}.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mdr-foot att2-foot">
        <span>Showing <b>{rows.length}</b> of <b>{data.rows.length}</b> marked · {data.unmarked.length} not marked</span>
        <span>Late arrivals are more than {data.policy.grace_minutes} minutes after the shift start</span>
      </div>

      {confirm && (
        <Modal className="mdl-modal" title={<><UserCheck size={18} /> Mark All Present</>} onClose={() => !busy && setConfirm(false)}>
          <div className="mdl-modal-body">
            <p className="mdl-modal-lead"><b>{data.unmarked.length}</b> active employee(s) with no attendance on <b>{fmtDay(day)}</b> will be marked <b>present</b> on their own shift ({shift} by default), each at their own site.</p>
            <div className="mdh-form-foot">
              <button type="button" className="mdr-btn" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
              <button type="button" className="mds-run" disabled={busy} onClick={markAllPresent}><Check size={14} /> {busy ? 'Marking…' : `Mark ${data.unmarked.length} Present`}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- mark / edit attendance
const STATUS_CHOICES = [['present', 'Present', 'user-check'], ['absent', 'Absent', 'user-x'], ['leave', 'Leave', 'tree-palm'],
  ['sick', 'Sick', 'thermometer'], ['holiday', 'Holiday', 'party-popper'], ['off', 'Day Off', 'moon']];
const mins = t => { const m = /^(\d{1,2}):(\d{2})$/.exec(t || ''); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };

// the server's rules (attendance_rules.work_out) against the company's default shift
function clockPreview(tin, tout, p) {
  const a = mins(tin), b = mins(tout), start = mins(p.shift_start), end = mins(p.shift_end);
  const out = {};
  if (a !== null && start !== null) {
    const lateBy = a - start;
    out.late = lateBy > p.grace_minutes;
    out.arrival = out.late ? `Late ${lateBy} min` : lateBy < 0 ? `Early ${-lateBy} min` : 'On time';
  }
  if (a !== null && b !== null) {
    if (b <= a) return { ...out, error: 'Time Out must be after Time In' };
    const span = (b - a) / 60;
    out.hours = hhmm(span - (span > p.break_after_hours ? p.break_minutes / 60 : 0));
    out.overtime = end !== null && b > end ? hhmm((b - end) / 60) : null;
  }
  return out;
}

function MarkModal({ data, day, record, onClose, onSaved, notify }) {
  const all = useMemo(() => [...data.unmarked.map(e => ({ ...e, marked: false })),
    ...data.rows.map(r => ({ id: r.employee_id, full_name: r.full_name, employee_no: r.employee_no, site_id: r.site_id, marked: true }))], [data]);
  const p = data.policy;
  const [f, setF] = useState(() => (record
    ? { employee_id: record.employee_id, site_id: record.site_id || '', status: record.status, time_in: record.time_in || '', time_out: record.time_out || '', reason: record.reason || '', ot_type: record.ot_type || 'weekday' }
    : { employee_id: '', site_id: '', status: 'present', time_in: p.shift_start, time_out: p.shift_end, reason: '', ot_type: 'weekday' }));
  const [busy, setBusy] = useState(false);
  const [editReason, setEditReason] = useState('');
  const set = (k, v) => setF(o => ({ ...o, [k]: v }));
  const pickEmployee = id => { const e = all.find(x => String(x.id) === id); setF(o => ({ ...o, employee_id: id, site_id: e?.site_id || o.site_id })); };
  const existing = record || data.rows.find(r => String(r.employee_id) === String(f.employee_id));
  const present = f.status === 'present';
  const save = async e => {
    e.preventDefault();
    if (!f.employee_id) { notify('Choose the employee to record', 'bad'); return; }
    setBusy(true);
    const body = { employee_id: f.employee_id, site_id: f.site_id || null, work_date: day, status: f.status,
      time_in: present ? f.time_in || null : null, time_out: present ? f.time_out || null : null, reason: f.reason || null, ot_type: f.ot_type };
    try {
      const res = existing
        ? await hr.updateAttendance(existing.id, { ...body, edit_reason: editReason.trim() || null })
        : await hr.markAttendance({ ...body, source: 'manual' });
      notify(res?.message || `Attendance ${existing ? 'updated' : 'recorded'}`);
      onSaved();
    } catch (err) { notify(err.message, 'bad'); }
    setBusy(false);
  };
  const who = all.find(x => String(x.id) === String(f.employee_id));
  const info = who && (data.rows.find(r => r.employee_id === who.id) || data.unmarked.find(u => u.id === who.id));
  const preview = present ? clockPreview(f.time_in, f.time_out, p) : null;
  const band = existing && (STATUS[shown(existing)] || STATUS.present);
  return (
    <Modal className="mdl-modal att2-markmodal" title={<><Icon name="clipboard-check" size={18} /> {existing ? 'Edit' : 'Mark'} Attendance — {fmtDay(day)}</>} onClose={onClose}>
      <form className="am-form" onSubmit={save}>
        <div className="am-emp">
          <span className="am-avatar">{who ? who.full_name.split(' ').slice(0, 2).map(w => w[0]).join('') : <Icon name="user-round" size={20} />}</span>
          <div className="grow">
            {record ? <b>{record.full_name}</b> : (
              <Select value={f.employee_id} required onChange={e => pickEmployee(e.target.value)} title="Employee">
                <option value="">— Select employee —</option>
                {data.unmarked.map(e => <option key={e.id} value={e.id}>{e.full_name} ({e.employee_no}) · not yet marked</option>)}
                {data.rows.map(r => <option key={r.employee_id} value={r.employee_id}>{r.full_name} ({r.employee_no}) · marked — edit</option>)}
              </Select>
            )}
            <small>{info ? [`#${info.employee_no}`, info.department].filter(Boolean).join(' · ') : 'Choose the employee to record'}</small>
          </div>
          {band && <span className={`xp-band ${band[1]}`}>{band[0].toUpperCase()}</span>}
        </div>
        {existing && !record && <div className="xp-note warn am-note">Already marked for this day — saving updates that record.</div>}

        <div className="am-sec"><span className="sec-icon"><Icon name="clipboard-check" size={16} /></span><b>Attendance Status</b></div>
        <div className="am-status">
          {STATUS_CHOICES.map(([k, label, icon]) => (
            <button key={k} type="button" className={`am-choice s-${k} ${f.status === k ? 'on' : ''}`} onClick={() => set('status', k)}>
              <Icon name={icon} size={17} /><span>{label}</span>
            </button>
          ))}
        </div>

        {present && <>
          <div className="am-sec"><span className="sec-icon"><Icon name="clock" size={16} /></span><b>Clock Times</b>
            <button type="button" className="btn small ghost" onClick={() => setF(o => ({ ...o, time_in: p.shift_start, time_out: p.shift_end }))}>Standard shift {t12(p.shift_start)}–{t12(p.shift_end)}</button></div>
          <div className="form-grid cols-2">
            <div className="field"><span className="field-label">Time In</span><input type="time" value={f.time_in} onChange={e => set('time_in', e.target.value)} /></div>
            <div className="field"><span className="field-label">Time Out</span><input type="time" value={f.time_out} onChange={e => set('time_out', e.target.value)} /></div>
          </div>
          <div className="am-preview">
            <div className={preview?.error ? 'bad' : ''}><span>Hours Worked</span><b>{preview?.error ? '—' : preview?.hours ?? '—'}</b></div>
            <div className={preview?.overtime ? 'ot' : ''}><span>Overtime</span><b>{preview?.overtime || '—'}</b></div>
            <div className={preview?.late ? 'late' : preview?.arrival ? 'ok' : ''}><span>Arrival</span><b>{preview?.arrival || '—'}</b></div>
          </div>
          {preview?.error && <small className="xp-hint bad">{preview.error}</small>}
          <div className="field"><span className="field-label">Overtime Type</span>
            <div className="am-seg">
              {[['weekday', 'Weekday'], ['weekend', 'Weekend'], ['holiday', 'Public Holiday']].map(([k, l]) => (
                <button key={k} type="button" className={f.ot_type === k ? 'on' : ''} onClick={() => set('ot_type', k)}>{l}</button>
              ))}
            </div>
          </div>
        </>}

        <div className="am-sec"><span className="sec-icon"><Icon name="map-pin" size={16} /></span><b>Location &amp; Remarks</b></div>
        <div className="form-grid cols-2">
          <div className="field"><span className="field-label">Site</span>
            <Select value={f.site_id} onChange={e => set('site_id', e.target.value)} title="Site"><option value="">— Select —</option>{data.sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></div>
          <div className="field"><span className="field-label">Reason / Remark</span>
            <input value={f.reason} placeholder={present ? 'Optional' : 'e.g. No show, Annual leave'} onChange={e => set('reason', e.target.value)} /></div>
          {existing && <div className="field span-2"><span className="field-label">Reason for Correction <b className="wh-req">*</b></span>
            <input value={editReason} placeholder="e.g. Verified against biometric, late clock-in adjusted" onChange={e => setEditReason(e.target.value)} />
            <small className="muted">A change of status, times or hours is recorded in the Attendance Edit Log and sent for approval.</small></div>}
        </div>
        {present && <p className="muted small am-foot">Preview uses {p.shift_start}–{p.shift_end}. On save, the employee's own work schedule is applied: overtime is time after the shift end, late is more than {p.grace_minutes} minutes after the start.</p>}
        <div className="form-actions"><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !!preview?.error}><Check size={15} /> {busy ? 'Saving…' : existing ? 'Update Attendance' : 'Save Attendance'}</button></div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- approval queue
const STEP = { pending: ['Pre-Approve', ShieldCheck], pre_approved: ['Review', Eye], reviewed: ['Final Approve', CheckCheck] };

function Queue({ notify, onOpen }) {
  const [days, setDays] = useState(14);
  const [filter, setFilter] = useState('open');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const load = useCallback(() => hr.listAttendanceBatches(days).then(setData).catch(e => notify(e.message, 'bad')), [days, notify]);
  useEffect(() => { setData(null); load(); }, [load]);
  if (!data) return <Loader title="Loading approval queue…" subtitle="Fetching site/day batches" />;
  const act = async (b, action, comment) => {
    const key = `${b.site_id}${b.date}`;
    setBusy(key);
    try {
      const res = await hr.attendanceBatchAction({ site_id: b.site_id, date: b.date, action, comment: comment || null });
      notify(`${b.site} · ${b.date}: ${BATCH[res.status]?.[0] || res.status}`);
      setRejecting(null); setReason('');
      await load();
    } catch (err) { notify(err.message, 'bad'); }
    setBusy(null);
  };
  const open = data.batches.filter(b => OPEN.includes(b.status));
  const list = data.batches.filter(b => filter === 'all' || (filter === 'open' ? [...OPEN, 'unsubmitted', 'rejected'].includes(b.status) : b.status === filter));
  const place = b => [b.state, b.site || 'Unassigned site'].filter(Boolean).join(' — ').toUpperCase();
  return (
    <div className="mdr-card att2-queue">
      <div className="mds-table-head">
        <b>{open.length} attendance batch{open.length === 1 ? '' : 'es'} awaiting approval action</b>
        <div className="att2-filters no-print">
          <Select value={filter} onChange={e => setFilter(e.target.value)} title="Show" style={{ minWidth: 170 }}>
            <option value="open">Needs action</option><option value="all">All</option>
            {Object.entries(BATCH).map(([key, [l]]) => <option key={key} value={key}>{l}</option>)}
          </Select>
          <Select value={String(days)} onChange={e => setDays(Number(e.target.value))} title="Period" style={{ minWidth: 130 }}>
            {[7, 14, 31, 62].map(n => <option key={n} value={String(n)}>Last {n} days</option>)}
          </Select>
        </div>
      </div>
      <div className="att2-flow no-print">
        {['pending', 'pre_approved', 'reviewed', 'approved'].map(s => <span key={s}><Chip tone={BATCH[s][1]}>{BATCH[s][0]}</Chip></span>)}
      </div>
      <div className="att2-batches">
        {list.map((b, i) => {
          const [label, tone] = BATCH[b.status];
          const key = `${b.site_id}${b.date}`;
          const step = STEP[b.status];
          return (
            <div key={key} className={`att2-batch t-${tone}`} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
              <div className="att2-batch-head">
                <b>{place(b)}</b><Chip tone={tone}>{label}</Chip>
              </div>
              <small className="att2-batch-meta">
                <span>Date: <b>{b.date}</b></span>
                {b.submitted_by && <span>Uploaded by: <b>{b.submitted_by}</b></span>}
                {b.reference && <span>Ref: <b className="mdr-id">{b.reference}</b></span>}
              </small>
              {(b.pre_approved_by || b.reviewed_by || b.approved_by) && (
                <small className="att2-batch-meta trail">
                  {b.pre_approved_by && <span>Pre-approved: <b>{b.pre_approved_by}</b></span>}
                  {b.reviewed_by && <span>Reviewed: <b>{b.reviewed_by}</b></span>}
                  {b.approved_by && <span>Approved: <b>{b.approved_by}</b></span>}
                </small>
              )}
              <div className="att2-batch-counts">
                {[['Total', b.total, ''], ['Present', b.present, 'g'], ['Absent', b.absent, 'rd'], ['Leave', b.leave, 'bl'], ['Late', b.late, 'am']]
                  .map(([l, v, c]) => <div key={l} className={c}><b>{fmtNumber(v)}</b><span>{l}</span></div>)}
              </div>
              {b.biometric > 0 && <small className="att2-batch-meta">+ {fmtNumber(b.biometric)} biometric record{b.biometric === 1 ? '' : 's'} for this site and day: approved automatically, not part of this batch</small>}
              <div className="att2-batch-actions">
                <button className="mds-view" onClick={() => onOpen(b.date)}><Eye size={13} /> View register</button>
                <div className="spacer" />
                {b.status === 'unsubmitted' && data.can_submit && <button className="mds-run sm" disabled={busy === key} onClick={() => act(b, 'submit')}><Send size={13} /> Submit for Review</button>}
                {b.status === 'rejected' && data.can_submit && <button className="mds-run sm" disabled={busy === key} onClick={() => act(b, 'submit')}><Send size={13} /> Resubmit</button>}
                {OPEN.includes(b.status) && data.can_approve && <button className="mdr-btn att2-reject" disabled={busy === key} onClick={() => setRejecting(b)}><X size={13} /> Reject</button>}
                {step && data.can_approve && <button className="mds-run sm" disabled={busy === key} onClick={() => act(b, 'approve')}>{React.createElement(step[1], { size: 13 })} {step[0]}</button>}
                {b.status === 'approved' && <Chip tone="green">✓ Approved</Chip>}
              </div>
            </div>
          );
        })}
        {!list.length && <div className="mdr-empty att2-empty">No batches in this view.</div>}
      </div>

      {rejecting && (
        <Modal className="mdl-modal" title={<><X size={18} /> Reject Attendance Batch</>} onClose={() => !busy && setRejecting(null)}>
          <div className="mdl-modal-body">
            <p className="mdl-modal-lead">Reject <b>{place(rejecting)}</b> attendance for <b>{rejecting.date}</b>? It goes back to the site to be corrected and resubmitted.</p>
            <div className="field"><span className="field-label">Reason <b className="wh-req">*</b></span>
              <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="What needs correcting" /></div>
            <div className="mdh-form-foot">
              <button type="button" className="mdr-btn" disabled={!!busy} onClick={() => setRejecting(null)}>Cancel</button>
              <button type="button" className="mds-run att2-danger" disabled={!!busy || !reason.trim()} onClick={() => act(rejecting, 'reject', reason.trim())}><X size={14} /> Reject Batch</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- analysis
function Analysis({ month: initial }) {
  const [month, setMonth] = useState(initial);
  const [a, setA] = useState(null);
  useEffect(() => { setA(null); hr.getAttendanceAnalysis(month).then(setA).catch(() => setA({ error: true })); }, [month]);
  return (
    <>
      <div className="mdr-card att2-analysis-head">
        <div><div className="mds-card-title"><BarChart3 size={17} /> Attendance Analytics</div><p>Monthly trends, site-wise analysis, overtime summary, lateness patterns</p></div>
        <div className="att2-day no-print">
          <button type="button" title="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}><ChevronLeft size={16} /></button>
          <span className="att2-month"><CalendarDays size={14} /> {monthLabel(month)}</span>
          <button type="button" title="Next month" onClick={() => setMonth(shiftMonth(month, 1))}><ChevronRight size={16} /></button>
        </div>
      </div>
      {!a && <Loader title="Loading analysis…" subtitle="Working out the month" />}
      {a?.error && <div className="error">Could not load the analysis.</div>}
      {a && !a.error && (
        <>
          <div className="md-kpis mds-kpis att2-kpis">
            {[['Attendance Rate', `${a.totals.attendance_rate}%`, Percent, 'green', 'Present of all records'], ['Records', a.totals.records, ClipboardList, 'blue', 'Marked this month'],
              ['Absences', a.totals.absent, UserX, 'red', 'Absent days'], ['Late Arrivals', a.totals.late, AlarmClock, 'amber', 'Late clock-ins'],
              ['Overtime Hours', a.totals.overtime_hours, Timer, 'purple', 'Total overtime']]
              .map(([label, value, Ico, tone, note], i) => <Kpi key={label} {...{ label, value, Ico, tone, note, i }} />)}
          </div>
          {!a.totals.records ? <div className="mdr-card mdr-empty">No attendance recorded in this month.</div> : (
            <>
              <div className="mdr-card att2-panel">
                <div className="mds-card-title">Daily Trend</div>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={a.trend.map(t => ({ ...t, day: t.date.slice(8) }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="day" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Legend />
                    <Bar dataKey="present" name="Present" stackId="a" fill="#16a34a" /><Bar dataKey="leave" name="Leave / Sick" stackId="a" fill="#3b82f6" />
                    <Bar dataKey="absent" name="Absent" stackId="a" fill="#dc2626" radius={[3, 3, 0, 0]} /><Line dataKey="late" name="Late" stroke="#d97706" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="att2-two">
                <div className="mdr-card att2-panel">
                  <div className="mds-card-title">Site-wise Attendance</div>
                  <div className="att2-sites">{a.sites.map(s => (
                    <div key={s.site}><span>{s.site}</span><i><em style={{ width: `${s.rate}%` }} /></i><b>{s.rate}%</b><small>{s.present}/{s.total}</small></div>
                  ))}</div>
                </div>
                <div className="mdr-card att2-panel">
                  <div className="mds-card-title">Overtime by Department</div>
                  {a.overtime.length ? (
                    <ResponsiveContainer width="100%" height={Math.max(180, a.overtime.length * 34)}>
                      <BarChart data={a.overtime} layout="vertical" margin={{ left: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={110} />
                        <Tooltip formatter={v => `${v} h`} /><Bar dataKey="hours" name="Overtime hours" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="mdr-empty">No overtime this month.</div>}
                </div>
              </div>
              <div className="mdr-card mdr-table-card">
                <div className="mds-table-head"><b>Lateness Pattern — most late arrivals</b></div>
                {a.late.length ? (
                  <div className="mdr-scroll att2-late">
                    <table className="mdr-table mds-table">
                      <thead><tr><th className="c">#</th><th>Employee ID</th><th>Employee Name</th><th>Department</th><th className="c">Late Days</th></tr></thead>
                      <tbody>{a.late.map((l, i) => <tr key={l.employee_id} className="mdr-row"><td className="c">{i + 1}</td><td className="mdr-emp">{l.employee_no}</td>
                        <td className="mdr-name">{l.full_name}</td><td>{l.department || '—'}</td><td className="c am">{l.count}</td></tr>)}</tbody>
                    </table>
                  </div>
                ) : <div className="mdr-empty">No late arrivals recorded this month. Lateness is detected when a Time In is recorded.</div>}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
